// Catalog.ID Client — Agents List Pane
// ============================================================================
// Lists agents owned by the current principal. Title row: create agent.

import { createListPaneType, createElement, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as agentApi from '../api/agent';
import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import * as tokenApi from '../api/token';
import { api } from '../api/client';
import { kemKeygen } from '../crypto/kem';
import { signingKeygen, signHybrid, wrapperToJson } from '../crypto/signing';
import { encryptClaim, reEncryptClaimKey } from '../crypto/claims';
import { decryptKeyBagWithKey, encryptEntry, setKeyBagEntry } from '../crypto/keybag';
import type { KeyBag, KeyBagEntry } from '../crypto/keybag';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import { downloadBlob } from '../components/pdf-loader';
import * as aes from '../crypto/aes';
import { buildAgentProvisioningSeed } from '../crypto/agent-seed';
import { getActiveCid } from '../state/device-storage';
import { keys } from '../state/keys';
import type { AgentSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

const TYPE_LABELS: Record<string, string> = {
    ai_model: 'AI Model',
    automation: 'Automation',
    human_operator: 'Human Operator',
};

const STATUS_COLORS: Record<string, string> = {
    active: 'rgba(80,200,80,0.3)',
    terminated: 'rgba(220,50,50,0.3)',
};

const columns: ColumnDef<AgentSummary>[] = [
    {
        key: 'cid',
        header: 'CID',
        width: 140,
        getValue: (item) => item.cid,
        type: { kind: 'text' },
    },
    {
        key: 'agentLabel',
        header: 'Label',
        width: 160,
        getValue: (item) => item.agentLabel,
        type: { kind: 'text' },
    },
    {
        key: 'agentType',
        header: 'Type',
        width: 120,
        getValue: (item) => item.agentType,
        type: {
            kind: 'badge',
            backgroundColor: () => 'rgba(176,128,224,0.3)',
            converter: (v) => TYPE_LABELS[v as string] ?? String(v),
        },
    },
    {
        key: 'status',
        header: 'Status',
        width: 100,
        getValue: (item) => item.status,
        type: {
            kind: 'badge',
            backgroundColor: (v) => STATUS_COLORS[v as string] ?? '#555',
            converter: (v) => (v as string) === 'active' ? 'Active' : String(v),
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createAgentsDataSource(): DataSource<AgentSummary> {
    return new DataSource<AgentSummary>({
        fetch: async (params): Promise<FetchResult<AgentSummary>> => {
            // Hide terminated agents — there's nothing the user can do with them,
            // and their usernames are permanently retired. The account row itself
            // is preserved server-side for username retirement (§3c.9), but the
            // UI should not clutter the list with dead entries.
            const agents = (await agentApi.listAgents()).filter(a => a.status !== 'terminated');
            const sliced = agents.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: agents.length };
        },
        keyFn: (item) => item.cid,
        pageSize: 100,
    });
}

const config: ListPaneConfig<AgentSummary> = {
    typeId: PANE_IDS.agentsList,
    defaultTitle: 'Agents',
    createDataSource: () => createAgentsDataSource(),
    columns,
    keyFn: (item) => item.cid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'agents') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.agentDetail,
            title: item.agentLabel || item.cid,
            context: { agentCid: item.cid },
        }, { originPaneId: ctx.instance.instanceId });
    },
    // The list's own verb belongs in its title row, not in a body toolbar
    // (shell USAGE.md "Where a button belongs").
    tabBarActions: (ctx, dataSource) => renderPaneActions([
        {
            key: 'create',
            label: '+ Create Agent',
            variant: 'primary',
            execute: () => void handleCreateAgent(ctx.shell, dataSource),
        },
    ]),
    ariaLabel: 'Agents list',
};

async function handleCreateAgent(shell: PaneTypeRenderContext['shell'], dataSource: DataSource<AgentSummary>): Promise<void> {
    const content = createElement('div', { className: 'cid-wizard-step' });

    content.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Create Agent' }));

    // Label
    const labelRow = createElement('div', { className: 'cid-auth-field' });
    labelRow.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: 'Agent Label' }));
    const labelInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    labelInput.type = 'text';
    labelInput.placeholder = 'e.g., Studio AI Assistant';
    labelRow.appendChild(labelInput);
    content.appendChild(labelRow);

    // Type select
    const typeRow = createElement('div', { className: 'cid-auth-field' });
    typeRow.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: 'Agent Type' }));
    const typeSelect = createElement('select', { className: 'cid-auth-input' }) as HTMLSelectElement;
    for (const [value, label] of Object.entries(TYPE_LABELS)) {
        const opt = createElement('option', { textContent: label }) as HTMLOptionElement;
        opt.value = value;
        typeSelect.appendChild(opt);
    }
    typeRow.appendChild(typeSelect);
    content.appendChild(typeRow);

    // Supersecret (needed to update the key bag)
    const ssRow = createElement('div', { className: 'cid-auth-field' });
    ssRow.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: 'Supersecret (to update your key bag)' }));
    const ss = createSupersecretTextarea();
    ssRow.appendChild(ss.element);
    content.appendChild(ssRow);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Create Agent',
    }) as HTMLButtonElement;
    content.appendChild(submitBtn);

    submitBtn.disabled = true;
    function refreshSubmit(): void {
        submitBtn.disabled = !labelInput.value.trim() || !ss.isValid();
    }
    labelInput.addEventListener('input', refreshSubmit);
    ss.onValidityChange(refreshSubmit);

    const modal = shell.showModal(content, { size: 'normal' });

    submitBtn.addEventListener('click', async () => {
        const label = labelInput.value.trim();
        if (!label || !ss.isValid()) return;
        const supersecret = ss.getValue();

        errorEl.classList.remove('cid-pane-error--visible');
        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Decrypting key bag...';

        try {
            // Decrypt the principal's keybag once to obtain the supersecret-
            // derived AES key. The keybag's salt is preserved — adding agent
            // entries does not require deriving a new key or re-encrypting
            // existing entries.
            const kbResp = await accountApi.getKeyBag();
            let bag: KeyBag;
            let derivedKey: Uint8Array;
            try {
                ({ bag, derivedKey } = await decryptKeyBagWithKey(kbResp, supersecret));
            } catch {
                throw new Error('Invalid supersecret — could not decrypt key bag');
            }

            // Generate agent key pairs. Per whitepaper §3c.6 / §3c.6a / §3c.6b the
            // agent owns its own `attr_encrypt`, `msg_encrypt`, `efs_encrypt`, and
            // Registry signing key. The first three are registered as
            // `accountpublickey` rows (so others can encrypt messages, claims, and
            // EFS-wrapped material to the agent); the Registry signing key is stored
            // as a `registry_signing_key` claim on the agent. All four private
            // halves live in the principal's key bag under `agent:{cid}:{slot}`.
            submitBtn.textContent = 'Generating agent keys...';
            const attrKP = kemKeygen();
            const msgKP = kemKeygen();
            const efsKP = kemKeygen();
            const sigKP = signingKeygen();

            // Encrypt the three private keys with the existing derived AES
            // key + fresh per-entry nonces. The encrypted plaintext is only
            // `{algorithm, privateKey}` — it does not depend on the agent
            // CID, so we can encrypt before the server assigns one. The
            // server prefixes `agent:{newAgentCid}:` to each suffix when
            // inserting into the keybag.
            submitBtn.textContent = 'Encrypting agent keys...';
            const attrPlain: KeyBagEntry = {
                keyId: 'placeholder',
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(attrKP.secretKey),
            };
            const msgPlain: KeyBagEntry = {
                keyId: 'placeholder',
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(msgKP.secretKey),
            };
            const efsPlain: KeyBagEntry = {
                keyId: 'placeholder',
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(efsKP.secretKey),
            };
            const sigPlain: KeyBagEntry = {
                keyId: 'placeholder',
                algorithm: 'Ed25519-MLDsa65',
                privateKey: aes.toBase64(sigKP.secretSeed),
            };
            const [encAttr, encMsg, encEfs, encSig] = await Promise.all([
                encryptEntry(attrPlain, derivedKey),
                encryptEntry(msgPlain, derivedKey),
                encryptEntry(efsPlain, derivedKey),
                encryptEntry(sigPlain, derivedKey),
            ]);

            // Atomic agent creation: account row, agent's accountpublickey
            // rows, and the principal-keybag entries all commit in one DB
            // transaction. No window in which the agent can exist without
            // its private keys being recoverable in the principal's bag.
            submitBtn.textContent = 'Creating agent...';
            const resp = await agentApi.createAgent({
                label,
                agentType: typeSelect.value,
                publicKeys: [
                    { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', publicKey: aes.toBase64(attrKP.publicKey) },
                    { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', publicKey: aes.toBase64(msgKP.publicKey) },
                    { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', publicKey: aes.toBase64(efsKP.publicKey) },
                ],
                principalKeyBagEntries: [
                    { suffix: 'attr_encrypt',      algorithm: 'X25519-MLKem1024', encryptedPrivateKey: encAttr.encryptedPrivateKey, nonce: encAttr.nonce },
                    { suffix: 'msg_encrypt',       algorithm: 'X25519-MLKem1024', encryptedPrivateKey: encMsg.encryptedPrivateKey,  nonce: encMsg.nonce },
                    { suffix: 'efs_encrypt',       algorithm: 'X25519-MLKem1024', encryptedPrivateKey: encEfs.encryptedPrivateKey,  nonce: encEfs.nonce },
                    { suffix: 'agent_signing_key', algorithm: 'Ed25519-MLDsa65',  encryptedPrivateKey: encSig.encryptedPrivateKey,  nonce: encSig.nonce },
                ],
            });
            const agentCid = resp.agentCid;

            // Register the Registry signing key as a `registry_signing_key`
            // claim on the agent account. This is a *publication* concern,
            // not an identity-existence concern: by the time we get here
            // the agent already exists and its private keys are in the
            // principal's bag. If this step fails, the agent is still
            // functional for KEM operations (decrypt claims shared with
            // the agent, receive encrypted messages); the principal can
            // retry the registry-key registration later.
            submitBtn.textContent = 'Registering Registry signing key...';
            const principalToken = api.getToken();
            const sigPubB64 = aes.toBase64(sigKP.publicKey);

            // The operator list must be fetched on the PRINCIPAL session — an
            // agent token gets 403 on /api/operators (default-deny). Do it now,
            // before we switch to the agent token below. Empty (no operator↔org
            // binding yet) is non-fatal: the agent is still created and its keys
            // downloadable; the registry key just can't be published until an
            // operator exists, so we surface a warning instead of aborting.
            let liveOperators: { cid: string; publicKey: string }[] = [];
            try {
                liveOperators = await publicClaimsApi.listOperators();
            } catch {
                liveOperators = [];
            }
            let publishWarning: string | null = null;

            try {
                const agentTokenResp = await tokenApi.agentDelegationGrant(
                    agentCid, ['registration_signing'],
                );
                api.setToken(agentTokenResp.accessToken, agentCid);

                // Claim value = agent's own signing public key, encrypted with
                // the agent's own attr_encrypt public key (§3c.6b).
                const encryptedClaim = await encryptClaim(
                    sigPubB64, attrKP.publicKey, `${label} — signing key`,
                );
                const claimResp = await claimsApi.createClaim({
                    claimType: 'registry_signing_key',
                    encryptedValue: encryptedClaim.encryptedValue,
                    nonce: encryptedClaim.nonce,
                    encryptedKey: encryptedClaim.encryptedKey,
                    encryptedLabel: encryptedClaim.encryptedLabel,
                    labelNonce: encryptedClaim.labelNonce,
                });

                // Ownership challenge-response, using the signing key we
                // just generated (still in memory).
                const { publicKey: validatorKemB64 } = await claimsApi.getValidatorKey();
                const validatorEncryptedKey = await reEncryptClaimKey(
                    encryptedClaim.encryptedKey,
                    attrKP.secretKey,
                    aes.fromBase64(validatorKemB64),
                );
                const validateResp = await claimsApi.validateClaim(claimResp.claimUuid, {
                    validationType: 'registry_signing_key',
                    encryptedKey: validatorEncryptedKey,
                });
                if (!validateResp.challenge) {
                    throw new Error('Validator did not return a challenge for the agent signing key');
                }
                const challengeBytes = new TextEncoder().encode(validateResp.challenge);
                const wrapper = signHybrid(challengeBytes, sigKP.secretSeed);
                const verifyResp = await claimsApi.verifyRegistryKey(
                    validateResp.challenge, wrapperToJson(wrapper), sigPubB64,
                );
                if (!verifyResp.success) {
                    // Validator rejected the proof — revert the agent claim out of
                    // "pending" (best-effort, still under the agent session that owns
                    // the claim) so it doesn't sit stuck.
                    try { await claimsApi.cancelValidation(claimResp.claimUuid); }
                    catch { /* best-effort cleanup */ }
                    throw new Error('Validator rejected the agent signing key ownership challenge');
                }

                // Publish the validated registry_signing_key so it lands in the
                // operator review queue. Without this the claim stays validated
                // but unpublished, and EFS's retrieval gate — which resolves the
                // agent's fingerprint through the *published* registry-key
                // directory — refuses every retrieval (the agent could hold its
                // keys and decrypt, but never pull a byte for rendering). Runs on
                // the agent token: the agent owns the claim. Non-fatal: a publish
                // failure (or no operators) leaves a validated claim the owner
                // can re-publish later, so it must not abort the seed download.
                try {
                    if (liveOperators.length === 0) {
                        publishWarning = 'Agent created, but its registry signing key '
                            + 'was not published: no operator is available yet. Once the '
                            + 'operator↔organization binding is in place, re-publish the '
                            + "agent's registry_signing_key so retrievals can be authorized.";
                    } else {
                        const detail = await claimsApi.getClaim(claimResp.claimUuid);
                        const selfKey = detail.keys.find(k => k.recipientType === 'self');
                        if (!selfKey) {
                            throw new Error('registry_signing_key claim has no self key to re-wrap');
                        }
                        const operatorKeys: { operatorCid: string; encryptedKey: string }[] = [];
                        for (const op of liveOperators) {
                            operatorKeys.push({
                                operatorCid: op.cid,
                                encryptedKey: await reEncryptClaimKey(
                                    selfKey.encryptedKey,
                                    attrKP.secretKey,
                                    aes.fromBase64(op.publicKey),
                                ),
                            });
                        }
                        await publicClaimsApi.publishClaim(claimResp.claimUuid, { operatorKeys });
                    }
                } catch (pubErr) {
                    publishWarning = 'Agent created and its registry signing key validated, '
                        + 'but publishing it to the operator review queue failed'
                        + (pubErr instanceof Error ? `: ${pubErr.message}` : '')
                        + '. Re-publish the registry_signing_key from the claims pane before '
                        + 'the agent needs to retrieve from EFS.';
                }
            } finally {
                // Always restore the principal session, even if claim setup
                // failed.
                if (principalToken) api.setToken(principalToken);
            }

            // Add the three new entries to the in-memory keybag so the agent's
            // keys are usable in the current session. These match the
            // server-side keyIds (`agent:{agentCid}:{suffix}`).
            bag = setKeyBagEntry(bag, {
                keyId: `agent:${agentCid}:attr_encrypt`,
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(attrKP.secretKey),
            });
            bag = setKeyBagEntry(bag, {
                keyId: `agent:${agentCid}:msg_encrypt`,
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(msgKP.secretKey),
            });
            bag = setKeyBagEntry(bag, {
                keyId: `agent:${agentCid}:efs_encrypt`,
                algorithm: 'X25519-MLKem1024',
                privateKey: aes.toBase64(efsKP.secretKey),
            });
            bag = setKeyBagEntry(bag, {
                keyId: `agent:${agentCid}:agent_signing_key`,
                algorithm: 'Ed25519-MLDsa65',
                privateKey: aes.toBase64(sigKP.secretSeed),
            });
            keys.load(bag);

            shell.toast(`Agent ${agentCid} created`, 'success');
            if (publishWarning) shell.toast(publishWarning, 'warning');
            dataSource.refresh();
            // Final step: offer the ONE-TIME provisioning-seed download while
            // the private keys are still in memory. After this modal closes
            // they exist only encrypted in the principal's keybag, which the
            // UI never exports.
            renderSeedDownloadStep(content, modal, agentCid,
                { attr: attrKP, msg: msgKP, efs: efsKP, signing: sigKP });
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Create Agent';
            errorEl.textContent = err instanceof Error ? err.message : 'Failed to create agent';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

/**
 * Success step of the create wizard: the agent exists, its keys are in the
 * keybag, and this is the ONLY moment the plaintext key material is available
 * for export — so the provisioning-seed download for a publisher server's
 * agent-runtime lives here.
 *
 * TEMPORARY (decision 2026-08-10): the seed file exists because agent-runtime
 * `provision` cannot yet fetch keys from the principal's keybag (its
 * ProvisionCommand marks keybag-fetch as future work — "option A"). When that
 * lands, remove this step and crypto/agent-seed.ts with it.
 */
function renderSeedDownloadStep(
    content: HTMLElement,
    modal: { close(): void },
    agentCid: string,
    keypairs: {
        attr: { publicKey: Uint8Array; secretKey: Uint8Array };
        msg: { publicKey: Uint8Array; secretKey: Uint8Array };
        efs: { publicKey: Uint8Array; secretKey: Uint8Array };
        signing: { publicKey: Uint8Array; secretSeed: Uint8Array };
    },
): void {
    content.replaceChildren();
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading', textContent: `Agent ${agentCid} created`,
    }));
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Its private keys are stored encrypted in your key bag. '
            + 'If this agent will run a publisher server, download the provisioning '
            + 'seed NOW — this is the only time it can be produced. On the server: '
            + 'agent-runtime provision --keys <file> (the setup script shreds the '
            + 'file once the key store is written); transfer it securely and delete '
            + 'any other copy.',
    }));
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Note: the seed file is a temporary mechanism — direct '
            + 'keybag-fetch provisioning (no file at all) is planned to replace it.',
    }));

    const row = createElement('div', { className: 'cid-auth-field' });
    const downloadBtn = createElement('button', {
        className: 'cs-btn cs-btn--small',
        textContent: 'Download provisioning seed',
    }) as HTMLButtonElement;
    downloadBtn.addEventListener('click', () => {
        void (async () => {
            const seed = await buildAgentProvisioningSeed({
                agentCid,
                principalCid: getActiveCid() ?? undefined,
                attr: keypairs.attr,
                msg: keypairs.msg,
                efs: keypairs.efs,
                signing: keypairs.signing,
            });
            downloadBlob(
                new Blob([JSON.stringify(seed, null, 2)], { type: 'application/json' }),
                `agent-${agentCid}.json`,
            );
        })();
    });
    row.appendChild(downloadBtn);
    content.appendChild(row);

    const closeBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Close',
    }) as HTMLButtonElement;
    closeBtn.addEventListener('click', () => modal.close());
    content.appendChild(closeBtn);
}

export function createAgentsListPane() {
    return createListPaneType(config);
}
